import React from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  defaultHeroOverlayConfig,
  HERO_OVERLAY_DEFAULT,
  HERO_OVERLAY_DIRECTIONS,
  HERO_OVERLAY_PRESETS,
  heroOverlayPresetId,
  parseHeroOverlay,
  serializeHeroOverlay,
} from "./heroOverlayGradient";

function GradientColorControl({ label, color, opacity, disabled, onColorChange, onOpacityChange }) {
  return (
    <Box
      sx={{
        p: 1.15,
        borderRadius: 2.5,
        bgcolor: "#F7F8FA",
        border: "1px solid rgba(25,36,47,.08)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          component="input"
          type="color"
          aria-label={label}
          value={color}
          disabled={disabled}
          onChange={(event) => onColorChange(event.target.value)}
          sx={{
            width: 44,
            height: 40,
            p: 0.2,
            border: "1px solid rgba(25,36,47,.14)",
            borderRadius: 2,
            bgcolor: "transparent",
            cursor: disabled ? "default" : "pointer",
            flexShrink: 0,
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 900, color: "#19242F" }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
            Tocá la muestra para elegir el tono.
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.95 }}>
        <Typography variant="caption" sx={{ minWidth: 92, color: "rgba(25,36,47,.64)", fontWeight: 850 }}>
          Opacidad {opacity}%
        </Typography>
        <Slider
          size="small"
          min={0}
          max={100}
          step={1}
          value={opacity}
          disabled={disabled}
          valueLabelDisplay="auto"
          valueLabelFormat={(next) => `${next}%`}
          aria-label={`Opacidad de ${label}`}
          onChange={(_, next) => onOpacityChange(Number(next))}
          sx={{ minWidth: 0, flex: 1 }}
        />
      </Stack>
    </Box>
  );
}

export default function HeroOverlayGradientEditor({ value, disabled = false, onChange }) {
  const parsed = React.useMemo(() => parseHeroOverlay(value), [value]);
  const enabled = parsed.kind !== "empty";
  const editable = parsed.kind === "editable";
  const config = editable ? parsed.config : null;
  const presetId = editable ? heroOverlayPresetId(config) : null;
  const currentIntensity = editable ? Math.max(config.startOpacity, config.endOpacity) : 0;

  const commitConfig = React.useCallback(
    (nextConfig) => onChange?.(serializeHeroOverlay(nextConfig)),
    [onChange]
  );

  const patchConfig = React.useCallback(
    (patch) => {
      if (!config) return;
      commitConfig({ ...config, ...patch });
    },
    [commitConfig, config]
  );

  const changeIntensity = React.useCallback(
    (nextValue) => {
      if (!config) return;
      const intensity = Math.max(0, Math.min(100, Math.round(Number(nextValue) || 0)));
      const current = Math.max(config.startOpacity, config.endOpacity);
      // Mantener la proporción del degradado cuando hay transparencia previa.
      // Desde cero, recuperar una transición suave en vez de una placa opaca.
      const ratio = current > 0
        ? { start: config.startOpacity / current, end: config.endOpacity / current }
        : { start: 0.2, end: 1 };
      commitConfig({
        ...config,
        startOpacity: Math.round(intensity * ratio.start),
        endOpacity: Math.round(intensity * ratio.end),
      });
    },
    [config, commitConfig]
  );

  const selectPreset = React.useCallback(
    (presetIdValue) => {
      const preset = HERO_OVERLAY_PRESETS.find((item) => item.id === presetIdValue);
      if (!preset) return;
      commitConfig(preset.config);
    },
    [commitConfig]
  );

  const enableOverlay = React.useCallback(() => {
    if (enabled) return;
    onChange?.(HERO_OVERLAY_DEFAULT);
  }, [enabled, onChange]);

  return (
    <Box
      sx={{
        p: 1.3,
        borderRadius: 3,
        bgcolor: "#fff",
        border: "1px solid rgba(25,36,47,.09)",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 950, color: "#19242F" }}>
          Capa sobre la imagen
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.2, color: "rgba(25,36,47,.62)" }}>
          Ajustá contraste y tono sin escribir código.
        </Typography>
      </Box>
      <FormControlLabel
        label="Mostrar capa sobre la imagen"
        labelPlacement="start"
        disabled={disabled}
        control={
          <Switch
            checked={enabled}
            onChange={(event) => {
              if (event.target.checked) enableOverlay();
              else onChange?.("");
            }}
            inputProps={{ "aria-label": "Mostrar capa sobre la imagen" }}
          />
        }
        sx={{
          mt: 0.75,
          ml: 0,
          mr: 0,
          width: "100%",
          justifyContent: "space-between",
          "& .MuiFormControlLabel-label": { fontSize: ".86rem", fontWeight: 850, color: "#19242F" },
        }}
      />

      {enabled ? (
        <Stack spacing={1.35} sx={{ mt: 1.35 }}>
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 0.6, color: "rgba(25,36,47,.62)", fontWeight: 850 }}>
              Vista de la capa
            </Typography>
            <Box
              aria-label="Muestra del gradiente de la portada"
              sx={{
                height: 46,
                borderRadius: 2.25,
                background: String(value || "transparent"),
                border: "1px solid rgba(25,36,47,.12)",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,.18)",
              }}
            />
          </Box>

          {editable ? (
            <Box sx={{ px: 0.35 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 900, color: "#19242F" }}>
                  Oscuridad de la imagen
                </Typography>
                <Typography variant="body2" sx={{ color: "#19242F", fontWeight: 900 }}>
                  {currentIntensity}%
                </Typography>
              </Stack>
              <Slider
                min={0}
                max={100}
                step={1}
                value={currentIntensity}
                disabled={disabled}
                valueLabelDisplay="auto"
                valueLabelFormat={(next) => `${next}%`}
                aria-label="Oscuridad de la imagen"
                onChange={(_, next) => changeIntensity(next)}
              />
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.60)" }}>
                0% deja ver la foto sin oscuridad; 100% usa la intensidad máxima del degradado.
              </Typography>
            </Box>
          ) : null}

          <Box>
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 900, color: "#19242F" }}>
              Atajos de intensidad
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              aria-label="Atajos de intensidad de la capa"
              value={presetId}
              onChange={(_, next) => next && selectPreset(next)}
            >
              {HERO_OVERLAY_PRESETS.map((preset) => (
                <ToggleButton
                  key={preset.id}
                  value={preset.id}
                  disabled={disabled}
                  sx={{ textTransform: "none", fontWeight: 850 }}
                >
                  {preset.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ display: "block", mt: 0.55, color: "rgba(25,36,47,.56)" }}>
              Son puntos de partida: después podés personalizar cada control.
            </Typography>
          </Box>

          {parsed.kind === "legacy" ? (
            <Alert severity="info" sx={{ borderRadius: 2.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 850 }}>
                Esta capa usa una configuración anterior que no puede representarse sin perder detalle.
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.35 }}>
                Se conserva intacta mientras no la reemplaces. Convertirla crea una capa editable equivalente al valor recomendado actual.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={disabled}
                onClick={() => commitConfig(defaultHeroOverlayConfig())}
                sx={{ mt: 1, textTransform: "none", fontWeight: 850 }}
              >
                Convertir a capa editable
              </Button>
            </Alert>
          ) : null}

          {editable ? (
            <>
              <GradientColorControl
                label="Color inicial"
                color={config.startColor}
                opacity={config.startOpacity}
                disabled={disabled}
                onColorChange={(nextColor) => patchConfig({ startColor: nextColor })}
                onOpacityChange={(nextOpacity) => patchConfig({ startOpacity: nextOpacity })}
              />
              <GradientColorControl
                label="Color final"
                color={config.endColor}
                opacity={config.endOpacity}
                disabled={disabled}
                onColorChange={(nextColor) => patchConfig({ endColor: nextColor })}
                onOpacityChange={(nextOpacity) => patchConfig({ endOpacity: nextOpacity })}
              />

              <FormControl size="small" fullWidth disabled={disabled}>
                <InputLabel id="hero-overlay-direction-label">Dirección</InputLabel>
                <Select
                  labelId="hero-overlay-direction-label"
                  label="Dirección"
                  value={config.direction}
                  onChange={(event) => patchConfig({ direction: event.target.value })}
                >
                  {HERO_OVERLAY_DIRECTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : null}
        </Stack>
      ) : (
        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: "rgba(25,36,47,.58)" }}>
          La portada se muestra sin ninguna capa de color sobre la imagen.
        </Typography>
      )}
    </Box>
  );
}
